/**
 * File-related components used across Mule Refactor slides
 */

import { useState } from 'react'
import { getDiffPath } from '~/utils/diffUtils'
import { Diff } from '~/protos/agents'
import { CheckCircleIcon, ChevronIcon, CodeIcon, DocumentIcon, MenuFileIcon } from './Icons'

interface FileListItemProps {
  /** Display name of the file */
  fileName: string
  /** Click handler */
  onClick: () => void
  /** Whether the item is selected */
  selected?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Single file list item component
 * Displays a file with icon and name, clickable
 */
export function FileListItem({
  fileName,
  onClick,
  selected = false,
  className = ''
}: FileListItemProps) {
  return (
    <div
      className={`bg-[rgba(0,0,0,0.02)] flex flex-col h-[48px] items-start px-0 py-[4px] rounded-lg w-full cursor-pointer hover:bg-[rgba(0,0,0,0.04)] transition-colors ${className}`}
      onClick={onClick}
    >
      <div className='flex flex-1 gap-[8px] items-center min-h-px min-w-px pl-[12px] pr-0 py-0 w-full'>
        <DocumentIcon selected={selected} className='flex-shrink-0' />
        <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] truncate">
          {fileName}
        </p>
      </div>
    </div>
  )
}

interface MoreFilesButtonProps {
  /** Number of additional files not shown */
  remainingCount: number
  /** Click handler */
  onClick: () => void
  /** Additional CSS classes */
  className?: string
}

/**
 * "View more files" button component
 * Shown when there are more files than the preview limit
 */
export function MoreFilesButton({ remainingCount, onClick, className = '' }: MoreFilesButtonProps) {
  return (
    <div
      className={`flex items-center justify-center w-full py-[8px] cursor-pointer hover:bg-[rgba(0,0,0,0.02)] rounded-[8px] transition-colors ${className}`}
      onClick={onClick}
    >
      <p className="font-['Geist'] font-medium text-[12px] text-[rgba(0,0,0,0.65)] hover:text-[rgba(0,0,0,0.88)]">
        +{remainingCount} more files • Click to view all
      </p>
    </div>
  )
}

/**
 * Args passed to ``FileDropdownProps.onFileClick``. Using an options
 * object (vs. positional ``(diffs, activePath)``) sidesteps TypeScript's
 * function-parameter bivariance: a handler typed as
 * ``(_: { diffs: Diff[] }) => void`` would NOT type-check against the
 * full ``FileClickArgs``, so a future caller that forgets ``activePath``
 * gets a compiler error rather than silently dropping the field.
 */
export interface FileClickArgs {
  /** All diffs in this dropdown. */
  diffs: Diff[]
  /** Path of the specific file the user clicked. */
  activePath: string
}

interface FileDropdownProps {
  /** Dropdown title */
  title: string
  /** Icon to display next to title (ReactNode) */
  icon?: 'document' | 'code' | 'checkCircle' | React.ReactNode
  /** Array of diffs to display */
  files: Diff[]
  /** Callback when a file is clicked. */
  onFileClick: (args: FileClickArgs) => void
  /** Whether dropdown is expanded by default */
  defaultExpanded?: boolean
  /**
   * Number of files at which the list switches from "render in flow" to
   * "render in a fixed-height scrollable container". All files are
   * always rendered into the DOM — this prop only controls scroll vs
   * inline layout, NOT a hard render cap. Renamed from
   * ``maxPreviewFiles`` because the old name suggested a slice cap that
   * the component never enforced.
   */
  scrollAfterFiles?: number
  /** Icon color for title */
  iconColor?: string
  /** Title color */
  titleColor?: string
  /** Whether the task is complete (controls checkCircle icon state) */
  isComplete?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Collapsible file dropdown component
 * Shows a list of files with expand/collapse functionality
 */
export function FileDropdown({
  title,
  icon = 'document',
  files,
  onFileClick,
  defaultExpanded = false,
  scrollAfterFiles = 3,
  iconColor = '#65558f',
  titleColor = '#65558f',
  isComplete = true,
  className = ''
}: FileDropdownProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const renderIcon = () => {
    if (icon === 'document') {
      return <DocumentIcon size={16} color={iconColor} />
    } else if (icon === 'code') {
      return <CodeIcon size={16} color={iconColor} />
    } else if (icon === 'checkCircle') {
      return <CheckCircleIcon size={16} checked={isComplete} />
    } else {
      return icon
    }
  }

  return (
    <div
      className={`flex flex-col gap-[12px] items-start bg-[rgba(0,0,0,0.02)] rounded-[24px] px-[24px] py-[16px] w-full ${className}`}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center justify-between w-full'
      >
        <div className='flex items-center gap-[16px]'>
          {/* Icon */}
          <div className='flex items-center justify-center rounded-[12px] size-[36px]'>
            {renderIcon()}
          </div>
          {/* Title */}
          <span
            className="font-['Geist'] font-medium text-[14px] leading-[22px]"
            style={{ color: titleColor }}
          >
            {title}
          </span>
        </div>
        {/* Chevron */}
        <ChevronIcon isExpanded={isExpanded} />
      </button>

      {isExpanded && (
        <>
          {/* Divider */}
          <div className='h-px w-full bg-[rgba(0,0,0,0.08)]' />

          {/* Files List */}
          <div className='flex flex-col gap-[12px] w-full px-[32px]'>
            <div
              className={`flex flex-col gap-[8px] px-[16px] ${
                files.length > scrollAfterFiles ? 'max-h-[180px] overflow-y-auto pr-[8px]' : ''
              }`}
            >
              {files.map((diff, index) => {
                const path = getDiffPath(diff)
                return (
                  <button
                    key={`${path}-${index}`}
                    onClick={() => onFileClick({ diffs: files, activePath: path })}
                    className='flex items-center py-[8px] text-left hover:bg-tokens-neutral-fill-fill-4 rounded-[6px] transition-colors'
                  >
                    <div className='flex items-center justify-center p-[4px] rounded-[4px] size-[32px] flex-shrink-0'>
                      <MenuFileIcon size={16} />
                    </div>
                    <span className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] truncate flex-1">
                      {/* Use the same `path` the click handler dispatches on
                          so DELETE diffs (where bFilePath is empty and
                          aFilePath is the canonical pre-deletion path)
                          display the same filename the user navigates to.
                          getDiffPath() encapsulates the bFilePath-vs-
                          aFilePath preference per diff type. */}
                      {path || 'Untitled'}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

interface FileListProps {
  /** Array of diffs to display */
  files: Diff[]
  /** Callback when any file is clicked (passes all files) */
  onFileClick: (diffs: Diff[]) => void
  /** Maximum number of files to show before "view more" button */
  maxDisplayFiles?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Simple file list component
 * Displays files with optional "view more" functionality
 */
export function FileList({
  files,
  onFileClick,
  maxDisplayFiles = 10,
  className = ''
}: FileListProps) {
  if (files.length === 0) {
    return (
      <div className='flex items-center justify-center w-full py-[24px]'>
        <p className="font-['Geist'] font-medium text-[14px] text-[rgba(0,0,0,0.45)]">
          No files generated yet
        </p>
      </div>
    )
  }

  return (
    <>
      {files.slice(0, maxDisplayFiles).map((diff, index) => {
        const fileName = diff.bFilePath || diff.aFilePath || `File ${index + 1}`
        const displayName = fileName.split('/').pop() || fileName

        return (
          <FileListItem
            key={index}
            fileName={displayName}
            onClick={() => onFileClick(files)}
            className={className}
          />
        )
      })}

      {files.length > maxDisplayFiles && (
        <MoreFilesButton
          remainingCount={files.length - maxDisplayFiles}
          onClick={() => onFileClick(files)}
        />
      )}
    </>
  )
}
